import { Customer } from "./customer";
import { ButlerType } from "./Enums";
import { FunctionProductCategory } from "./FunctionProductCategory";
import { MembershipRewardInfoRequest } from "./Membership/MembershipRewardInfoRequest";
import { CourseGroup, CourseGroupAwayLog, MenuHeadingGroupType, MenuOrderingStyle } from "./Menu";
import { PaymentPriceAdjustmentPayloads } from "./Payments/PaymentPriceAdjustmentPayload";
import { PickupDeliverySchedule } from "./PickupDeliverySchedule";
import { NameMap } from "./product";

export enum OrderSource {
  Till,
  OrderingWidget,
  TakeawayWidget,
  QRCode,
}

export enum OrderType {
  TableService,
  CounterService,
  Pickup,
  Delivery,
}

export enum ItemDispositionType {
  Transfer = "Transfer", // denotes items such as OrderItems were removed from original Order and transferred to another Order
  Void = "Void", // denotes items eg OrderItems were removed from original Order and poof went nowhere
}

export interface Orders {
  [orderId: string]: Order;
}

export interface Order {
  orderItems: OrderItems;
  date: string;
  /**
   * @deprecated Use {@link Order.firstName} and {@link Order.lastName} instead.
   */
  name?: string;
  firstName?: string; // firstName of customer who ordered this
  lastName?: string; // lastName of customer who ordered this
  uid?: string;
  bookingCustomerName?: string;
  pax?: number;
  svip?: boolean;
  vip?: boolean;
  number?: string;
  intervalId?: number;
  restaurantId: string;
  menuId: string;
  bookingId?: string;
  refBookingId?: string; // refBookingId belongs to booking that paid for pay laters in this booking
  note?: string;
  functionBookingId?: string;
  bookingRef?: string;
  mealId?: string;
  customerId?: string;
  // guestId?: string;
  orderSource: OrderSource;
  orderType: OrderType;
  orderNumber: number;
  createdAt: number;
  fcmToken?: string;
  _key?: string;
  operatorId?: string;
  tillId?: string;
  isManualCharge?: boolean;
  schedule?: PickupDeliverySchedule;
  tableNumber?: string;
  batchingTableGroupId?: string;
  orderedFromDifferentBookingTable?: boolean;
  bucketIds?: { [bucketId: string]: boolean } /** Currently only used for QR Ordering only orders when doing casual batching only.  */;
  hasPaidMenuPlan?: boolean /** denotes this order has at least onne pre paid menu plan item */;
}

export interface OrderItems {
  [orderItemId: string]: OrderItem;
}

export interface OrderModifier {
  productId: string;
  productSizeId: string;
  quantity?: number;
  name: string;
}

export interface OrderModifiers {
  [modifierGroupId: string]: OrderModifier;
}

export interface OrderPreparations {
  [id: string]: { id: string; name: string }[];
}

export interface OrderAdditions {
  [additionGroupId: string]: OrderAdditionProduct;
}

export interface OrderAdditionProduct {
  [additionProductId: string]: OrderItem;
}

export enum OrderItemStatus {
  InBasket, // Item is in basket
  WaitingToBeSent, // Item is sent from basket but still not sent to kitchen
  Sent, // Item is sent to kitchen
  Prepared, // Kitchen has prepared the item
  Served, // Kitchen has served and completed the item from their part
}

export enum OrderMode {
  PreService,
  InService,
}

export interface OrderItemUpsells {
  [upsellGroupId: string]: {
    [productId: string]: OrderItem;
  };
}

export interface OrderItemUpgrades {
  [upgradeGroupId: string]: {
    [productId: string]: OrderItem;
  };
}

export interface OverrideHeadings {
  [orderItemId: string]: {
    productId: string;
    overrideHeadingIds: string[];
    overrideHeadingId: string;
  };
}

export interface ItemOverrideCourseGroup {
  productId: string;
  overrideCourseGroupsOptions: CourseGroup[];
  overrideCourseGroupId: string;
}
export interface ItemOverrideCourseGroups {
  [orderItemId: string]: ItemOverrideCourseGroup;
}

/** both OrderItem and DocketItem stem from this */
export interface OrderItemBase {
  name: string /** if orderitem its product name , if docket item its docketname */;
  orderId: string;
  orderItemId: string;
  orderStatus: OrderItemStatus; // maintain orderStatus in Docket to make it less dependant on Order and OrderItem
  productId: string;
  quantity: number;
  sizeId: string;

  addedToBasket?: boolean; // denotes menu plan item is moved to basket for checkout and payment
  courseGroupKey?: string;
  isMenuPlanItem?: boolean; // denotes item was created as part of menu plan
  note?: string;
  paidQuantity?: number;
  parentComboOrderItemId?: string;
  position?: string;
  preparedTime?: number;
  completedTime?: number;
  membershipOrderItemInfo?: MembershipRewardInfoRequest; // should not have reward data
  /**
   * @deprecated
   * list of similar OrderItem to this OrderItem like menuHeadingId, courseGrooup, productId, size, modifiers, notes ...
   * except customerId, orderId and orderItemId
   */
  consolidatedItems?: Array<OrderItemBase>;
}

export interface OrderItem extends OrderItemBase {
  _key?: string;
  orderMode?: OrderMode;
  docketName: string;
  payLater?: boolean;
  /** @deprecated */
  payNow?: boolean;
  time: number;
  isComboQuantityIndependentFromOrderItem?: boolean;
  modifiers?: OrderModifiers;
  additions?: OrderAdditions;
  upsells?: OrderItemUpsells;
  upgrades?: OrderItemUpgrades;
  customerId?: string;
  preparations?: OrderPreparations;
  comboGroup?: OrderItemProducts;
  headingId: string;
  groupHeadingId?: string;
  isSpecialProduct: boolean;
  inclusive: boolean;
  inclusiveProduct?: boolean;
  overrideOrderingStyle?: MenuOrderingStyle;
  createdAt: number;
  menu: string;
  functionProductCategory?: FunctionProductCategory;
  packageId?: string;
  subHeadingId?: string;
  menuOptionId?: string;
  manualAmount?: number;
  beverageMenuPackageId?: string;
  addedAsUpsell?: boolean;
  tables?: string[];
  /** @deprecated */
  isCheckoutItem?: boolean;
  useComboChildItemsAsSeparateForDocket?: boolean;
  hidden?: boolean; // used to hide combo items from basket and product page
  butlerItemInfo?: {
    subCategoryId: string;
    butlerTypeId: ButlerType;
    categoryId: string;
  }; //exits if this is from butler items
  invoiceId?: string;
}

export enum DocketPrintStatus {
  NotPrinted = 0,
  Printed = 1,
  Error = 2,
}

export interface DocketItems {
  [docketItemId: string]: DocketItem;
}

export enum DocketType {
  Standard = 0,
  Removed = 1,
  Transferred = 2,
  NoteOnly = 3,
}

interface BaseDocket {
  _key?: string;
  id?: string;
  bookingId?: string;
  printerAreaIds: string[];
  functionBookingId?: string;
  notificationDeleted?: boolean;
  bookingRef?: string;
  bucketId?: string;
  batchSettingId?: string;
  createdAt: number;
  customerName: string;
  date: number;
  docketGenerationTime: number;
  orderSource: OrderSource;
  orderType: OrderType;
  pax?: number;
  printStatus: DocketPrintStatus;
  restaurantId: string;
  tableNo?: string;
  time: string;
  orderNumber?: number;
  courseGroups?: Array<string>;
  hidden: {
    one: boolean;
    prep: boolean;
    serve: boolean;
  };
  canUseMenuHeadingToGroup: boolean;
  operatorId?: string;
  customerId?: string;
  bookingTableNo?: string;
  orderId?: string;
  schedule?: PickupDeliverySchedule;
  note?: string;
  transferredFromTableNo?: string; // table no in transferred docket from where docket is transferred from
}

export interface NoteOnlyDocket extends BaseDocket {
  type: DocketType.NoteOnly;
  printing: DocketItemPrinting[];
  noteOnlyStatus: OrderItemStatus;
}

export interface KitchenOrderDocket extends BaseDocket {
  type: Exclude<DocketType, DocketType.NoteOnly>;
  docketItems: DocketItems;
}

export type Docket = NoteOnlyDocket | KitchenOrderDocket;

export interface DocketItemCombo extends Pick<DocketItem, "additions" | "name" | "preparations" | "modifiers" | "quantity" | "size" | "printing" | "hideSize"> {
  name: string;
}

export interface DocketItemPrinting {
  macAddress: string;
  printStatus: DocketPrintStatus;
  reason?: string;
  printFailedCount?: number;
  printSuccessCount?: number;
}
export interface DocketItemModifier {
  name: string;
  quantity: number;
  size?: string;
  hideSize: boolean;
  printing: DocketItemPrinting[];
}

export interface DocketItemPreparation {
  name: string;
  quantity: number;
  size?: string;
}
export type DocketItemAddition = Pick<DocketItem, "additions" | "name" | "preparations" | "modifiers" | "quantity" | "size" | "printing" | "hideSize"> & {
  hideQuantity: boolean;
};
export interface DocketItem extends OrderItemBase {
  additions: DocketItemAddition[];
  combo: DocketItemCombo[];
  courseGroupIndex?: number;
  customerNames?: { [customerId: string]: string };
  groupType?: MenuHeadingGroupType;
  heading: string;
  hideSize?: boolean;
  modifiers: DocketItemModifier[];
  parentProductName?: string;
  preparations: DocketItemPreparation[];
  printing: DocketItemPrinting[];
  size: string;
  tables?: string[];
  tableNumber: string;
  previousTableNumbers?: NameMap<string>; // to track changes in table numbers { String(Date.now()): tableNumber}
  orderItemCreatedAt?: number;
  isComboQuantityIndependentFromOrderItem?: boolean;
  docketItemId: string;
}

/**
 * @todo: this will be removed later when the feature on ordering app to enter table number will be developed so that all ordering app session will have tableNumber and table group id
 * */
export const DefaultIdForEmptyTableGroupId = "emptyTableGroupId";

export interface BucketsDoc {
  data: Buckets;
  date: number;
  restaurantId: string;
  hasIncompleteFosBuckets?: boolean;
}
export interface Buckets {
  [id: string]: Bucket;
}

export interface Bucket {
  bookingId: string;
  batchingTableGroupId: string;
  batchSettingId: string;
  completed?: boolean;
  completedAt?: number;
  expiryTime: number;
  menuId: string;
  startTime: number;
  time: number;
  timeExtensions?: Array<number>;
  isCasual?: boolean;
  id?: string;
}

export interface OrderingPrompt {
  courseGroupId: string;
  readByCustomers?: { [customerId: string]: boolean };
  createdAt: number;
}

/**
 * Static Course Group to be used in till if there is no fixed order of service menu. Its mocking the structure of courseGroups in batchSetting.
 * menuHeadngIds is there just to mock the batchSettings coursegroup structure
 */
export const manualCourseGroups = Object.freeze([
  { _key: "1", name: "1st Course", courseAbbreviation: "1", order: 1, menuHeadingIds: [] },
  { _key: "2", name: "2nd Course", courseAbbreviation: "2", order: 2, menuHeadingIds: [] },
  { _key: "3", name: "3rd Course", courseAbbreviation: "3", order: 3, menuHeadingIds: [] },
]);

export interface AccumulatedOrderItemsByMenuHeading {
  [menuHeadingId: string]: { paidQuantity: number; quantity: number };
}

export interface DeletedOrders {
  [deletedOrderId: string]: DeletedOrder;
}

export interface DeletedOrder extends Omit<Order, "orderItems"> {
  orderItems: DeletedOrderItems;
}

export interface DeletedOrderItems {
  [orderItemId: string]: DeletedOrderItem;
}

export interface DeletedOrderItem extends OrderItem {
  deletionLogs: { deletedReason?: string; deletedAt: number; deletedBy: string; deletedQuantity: number }[];
  type: ItemDispositionType;
}

export interface BookingOrderingNextCourseAwayData {
  currentOrderingCourseAway: string;
  previousOrderingCourseAway: string;
  courseAwayLogs: CourseGroupAwayLog[];
}

export interface BookingOrderingData {
  _key?: string;
  date: number;
  mealId: string;
  bookingId?: string;
  functionBookingId?: string;
  restaurantId: string;
  tableNumber?: string;
  nextCourseAwayData?: BookingOrderingNextCourseAwayData;
  orderingPrompts?: { [id: string]: OrderingPrompt };
  paxCount?: number;
  paxCountLogs?: { operatorId: string; createdAt: number; paxCount: number; paxChanged: number }[];
  appliedMinimumSpendAmount?: number;
  createdAt: number;
  updatedAt?: number;
  lockPayment?: boolean;
}

export interface BookingOrdersPriceAdjustmentDataMap {
  [bookingId: string]: BookingOrdersPriceAdjustmentData;
}
export interface BookingOrdersPriceAdjustmentData {
  _key?: string;
  date: number;
  mealId: string;
  bookingId?: string;
  functionBookingId?: string;
  restaurantId: string;
  paymentPriceAdjustmentPayload: PaymentPriceAdjustmentPayloads;
  updatedAt?: number;
}

export interface OrderItemProducts {
  [productId: string]: OrderItem;
}

export interface BatchingBuckets {
  [bucketId: string]: Bucket;
}

export interface MenuPlans {
  [customerId: string]: {
    customer: Customer;
    orderItems: OrderItem[];
  };
}

export type OrderItemFilterValue<T> = T | ((value: T | undefined, orderItem: OrderItem) => boolean);

export type OrderItemFilters = {
  [K in keyof OrderItem]?: OrderItemFilterValue<OrderItem[K]>;
};

export type GroupedEntity<T> = { [groupName: string]: T[] };

export type CloneOrderForTransferOptions = {
  orderId: string; // new OrderId to assign
  orderNumber: number; // new OrderNumber to assign
  bookingId: string; // new BookingId to assign
  functionBookingId: string;
  bookingRef: string; // new BookingRef to assign
  bookingTableNumber?: string;
  customerId: string; // new CustomerId to assign
  customerFirstName: string;
  customerLastName: string;
  idGenerator: () => string;
  keepOriginalOrderItemId?: boolean;
};

export type TransferOrderItemOption = {
  quantity: number;
  position?: string;
  note?: string;
  comboOrderItems?: TransferOrderItemOptions;
};
export type TransferOrderItemOptions = {
  [id: string]: TransferOrderItemOption;
};

export interface PaymentPriceAdjustmentResult {
  transferAdjustments: PaymentPriceAdjustmentPayloads;
  updatedSourceDocuments: {
    [docId: string]: PaymentPriceAdjustmentPayloads;
  };
}
